/* eslint-disable react/require-default-props */
// eslint-disable-next-line react-hooks/exhaustive-deps
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { ManagementState } from 'store/modules/management_store/types';
import ModalReactivateManagement from 'components/atoms/modal_reactivate_management';
import { createManaged, getBiddingSummary } from 'store/modules/management_store/actions';
import iconIA from 'assets/images/ia/icon-visao.svg';
import { ProfileState } from 'store/modules/profile_store/types';
import companyId from 'services/company_id';
import createTrack from 'services/actions/tracker_actions';

interface ControlManagementButtonProps {
  id: number;
  setModalFinishBiddingOpened: React.Dispatch<React.SetStateAction<boolean>>;
  setModalRemoveBiddingOpened: React.Dispatch<React.SetStateAction<boolean>>;
  setOverviewOpen: React.Dispatch<React.SetStateAction<boolean>>;
  managedByMe?: boolean;
}

const ControlManagementButton = (props: ControlManagementButtonProps) => {
  const { management, request } = useSelector<ApplicationState, ManagementState>(
    (state) => state.session.management,
  );

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const dispatch = useDispatch();

  const {
    id,
    setModalFinishBiddingOpened,
    setModalRemoveBiddingOpened,
    setOverviewOpen,
    managedByMe = false,
  } = props;

  // Na tela de detalhes as listas paginadas (management.managed / finished) não são
  // carregadas; o estado de gerenciamento vem do bidding_summary. Mantemos o fallback
  // nas listas para refletir as atualizações otimistas em outras telas.
  const biddingSummary = management.bidding_summary?.[id];

  const isManaged =
    (biddingSummary?.is_managed_by_me ?? false) ||
    !!management.managed.find((managed) => managed.id === id)?.managed;

  const [finished, setFinished] = useState<boolean>(
    !!biddingSummary?.finished || !!management.finished.find((item) => item.id === id),
  );

  const [modalReactivateManagementOpened, setModalReactivateManagementOpened] =
    useState<boolean>(false);

  useEffect(() => {
    setFinished(!!biddingSummary?.finished || !!management.finished.find((item) => item.id === id));
  }, [biddingSummary?.finished, management.finished]);

  // Após criar/finalizar/reativar/remover gerenciamento, recarrega o resumo para que os
  // botões reflitam o novo estado (as ações atualizam apenas os slices paginados, vazios
  // nesta tela). Ignora o primeiro render, que já é coberto pelo fetch da página.
  const didMountRef = useRef<boolean>(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (id) dispatch(getBiddingSummary(id, companyId(profile)));
  }, [request.managed.loaded, request.finish_managed.loaded, request.reactivate_managed.loaded]);

  return (
    <>
      {(isManaged || finished) && (
        <>
          {management?.tasks?.length ?? 0 >= 3 ? (
            <div className="btn-group" role="group">
              <button
                type="button"
                className="btn btn-secondary btn-ia d-flex align-items-center btn-sm"
                onClick={() => {
                  createTrack(`Gerenciar Licitações - Clicou resumo IA`);
                  setOverviewOpen((prev) => !prev);
                }}
                style={{ gap: 8 }}
              >
                <img
                  alt="Ícone IA"
                  src={iconIA}
                  height="20"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
                <span>Visão Geral</span>
              </button>
            </div>
          ) : null}
          {!finished && (
            <button
              className="btn btn-sm btn-secondary ml-2"
              type="button"
              onClick={() => setModalFinishBiddingOpened(true)}
            >
              Finalizar
            </button>
          )}
          {finished && (
            <button
              className="btn btn-sm btn-secondary"
              type="button"
              onClick={() => setModalReactivateManagementOpened(!modalReactivateManagementOpened)}
            >
              Reativar gerenciamento
            </button>
          )}
          {(isManaged || managedByMe) && (
            <button
              className="btn btn-sm btn-danger mx-2"
              type="button"
              onClick={() => {
                setModalRemoveBiddingOpened(true);
              }}
            >
              Remover do Gerenciamento
            </button>
          )}
          <ModalReactivateManagement
            modalReactivateManagementOpened={modalReactivateManagementOpened}
            setModalReactivateManagementOpened={setModalReactivateManagementOpened}
            biddingId={id}
          />
        </>
      )}
      {!isManaged && !finished && (
        <button
          type="button"
          className="btn btn-sm btn-primary ml-2"
          key="btn-add-management-bidding"
          onClick={() => {
            dispatch(createManaged(id, companyId(profile)));
          }}
        >
          Gerenciar esta licitação
        </button>
      )}
    </>
  );
};

export default ControlManagementButton;
